import React from "react";
import { useSearchParams } from "react-router-dom";

import { Chart } from "@/common/components/chart/chart";
import { useCurrency } from "@/common/hooks/use-currency";
import {
  DateRangeParams,
  useDateRangeParams,
} from "@/common/hooks/use-date-range-params";
import { invariant } from "@/common/utils/invariant";

import { QuickFilters } from "../quick-filters/quick-filters";
import {
  Filter,
  getAvailableFilters,
  getFilterByDateRange,
  getFilterParams,
  getURLParams,
} from "../quick-filters/quick-filters.utils";

const PriceChart = ({
  prices,
}: {
  prices: Array<{
    datetime: string;
    rate: number;
  }>;
}) => {
  const [, currency] = useCurrency();
  const dateFilters = getAvailableFilters();
  const searchParams = useDateRangeParams();
  const onDateFilterClick = useFilterClickHandler(dateFilters);
  const currentDateFilter = getFilterByDateRange(
    dateFilters,
    getFilterParams(searchParams as DateRangeParams),
  );
  invariant(currentDateFilter?.dateFrom);
  invariant(currentDateFilter.dateTo);

  return (
    <Container>
      <QuickFilters
        activeFilter={currentDateFilter}
        filters={dateFilters}
        onClick={onDateFilterClick}
      />
      <Chart
        data={{
          current: prices,
        }}
        // @ts-expect-error discrepancy between CF API and IDSign API
        currency={currency}
      />
    </Container>
  );
};

const Container = ({ children }: React.PropsWithChildren) => {
  return <div className="flex flex-col gap-y-8">{children}</div>;
};

const useFilterClickHandler = (filters: Filter[]) => {
  const [, setSearchParams] = useSearchParams();

  return React.useCallback(
    (index: number) => {
      const urlParams = getURLParams(filters[index]);
      setSearchParams((cur) => {
        Object.entries(urlParams).forEach(([key, value]) =>
          cur.set(key, value),
        );
        return cur;
      });
    },
    [filters, setSearchParams],
  );
};

PriceChart.Container = Container;

export { PriceChart };
